import React, { useCallback, useEffect, useMemo } from "react";
import HonorBoardCard from "../components/cards/HonorBoardCard";
import MyPostsStatisticsCard from "../components/cards/MyPostsStatisticsCard";
import {
  useGetStatisticsQuery,
  useLazyListPostsQuery,
} from "../services/users/community";
import { useLazyAdminListOrganizationPostsQuery } from "../services/organization/adminUsers";
import { useGetUserLeaderboardQuery } from "../services/users/leaderboard";
import { useGetAdminLeaderboardQuery } from "../services/organization/leaderboard";
import CommunityFeed from "./CommunityFeed";
import LoadingIndicator from "../components/LoadingIndicator";
import { useTranslation } from "react-i18next";

const Community = () => {
  const isAdmin = useMemo(
    () => localStorage.getItem("is_admin") === "true",
    [],
  );

  const { data: statistics, isLoading: isStatsLoading } = useGetStatisticsQuery(
    undefined,
    { skip: isAdmin },
  );

  const [listOrgPosts, orgPostsResult] =
    useLazyAdminListOrganizationPostsQuery();
  const [listEmployeePosts, employeePostsResult] = useLazyListPostsQuery();

  const listPosts = useCallback(
    (page = 1) => {
      if (isAdmin) return listOrgPosts({ page });
      return listEmployeePosts(page);
    },
    [isAdmin, listOrgPosts, listEmployeePosts],
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) listPosts(1);
  }, [listPosts]);

  const posts = isAdmin ? orgPostsResult.data : employeePostsResult.data;

  const { data: employeeLeaderboard } = useGetUserLeaderboardQuery(
    { page: 1, perPage: 3 },
    { skip: isAdmin },
  );

  const { data: adminLeaderboard } = useGetAdminLeaderboardQuery(
    { page: 1, perPage: 3 },
    { skip: !isAdmin },
  );

  const { t } = useTranslation(["common", "communityFeed"]);

  if (!isAdmin && isStatsLoading) return <LoadingIndicator />;

  const leaderboardPayload = isAdmin ? adminLeaderboard : employeeLeaderboard;

  const feed = (
    <div className="row community-page g-3">
      <div className="col-lg-8 col-md-12">
        <CommunityFeed
          listPosts={listPosts}
          posts={posts}
          interactionMode={isAdmin ? "organization" : "employee"}
          skipInitialFetch
        />
      </div>
      <div className="col-lg-4 col-md-12">
        <ul className="list-unstyled mb-0">
          {!isAdmin && (
            <li className="mb-3">
              <MyPostsStatisticsCard statistics={statistics?.data} />
            </li>
          )}

          <li className="mb-3">
            <HonorBoardCard
              leaderboardUsers={leaderboardPayload?.data}
              isAdmin={isAdmin}
              currentUserOrder={leaderboardPayload?.current_user_order}
            />
          </li>
        </ul>
      </div>
    </div>
  );

  // Admin: match discover-courses / other admin pages (full-width content-page shell)
  if (isAdmin) {
    return (
      <div className="admin-community-page">
        <header className="admin-community-page__toolbar">
          <div className="admin-community-page__intro">
            <h1 className="title-page mb-0">{t("allPosts")}</h1>
            <p className="mb-0 font_18 font-color-grey mt-2">
              {t("communityFeed:communityFeedDescription")}
            </p>
          </div>
        </header>

        <div className="admin-community-page__body">{feed}</div>
      </div>
    );
  }

  // Employee: centered shell with create-post actions
  return (
    <div className="container-fluid community-page-shell">
      <div className="row justify-content-center p-4">
        <div className="col-lg-10 col-md-12">
          <div className="d-flex flex-wrap community-page-shell__toolbar mb-4">
            <div className="community-page-shell__intro">
              <h1 className="mb-0 font_24 font-extrabold">{t("allPosts")}</h1>
              <p className="mb-0 font_16 font-color-grey">
                {t("communityFeed:communityFeedDescription")}
              </p>
            </div>
            <div className="community-page-shell__actions">
              <div className="create-post-button">
                <button
                  type="button"
                  className="btn btn-main-color font-15 w-100"
                  data-bs-toggle="modal"
                  data-bs-target="#create-post-button"
                >
                  <span
                    className="fas fa-plus fa-fw me-2"
                    aria-hidden="true"
                  ></span>
                  {t("communityFeed:addPost")}
                </button>
              </div>
            </div>
          </div>

          {feed}
        </div>
      </div>
    </div>
  );
};

export default Community;
